// ===================================================================
// 👥 USUÁRIOS DA INSTITUIÇÃO - Master cadastra apenas Admin
// Padrão igual ao UserManager do admin
// ===================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import { validateNewPassword, PASSWORD_POLICY_HINT } from '../../utils/passwordPolicy';
import { useNotifications } from '../../contexts/NotificationContext';

const EyeToggle = ({ visible, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    tabIndex={-1}
    style={{
      position: 'absolute',
      right: '10px',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '4px',
      color: '#6B7280'
    }}
    title={visible ? 'Ocultar senha' : 'Ver senha'}
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      {visible ? (
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      ) : (
        <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>
      )}
    </svg>
  </button>
);

export const InstitutionUsersPage = React.memo(({ institution, onBack }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { addNotification } = useNotifications();

  const loadUsers = useCallback(async () => {
    if (!institution?.id) return;
    try {
      setLoading(true);
      const data = await apiClient.getMasterInstitutionUsers(institution.id);
      const all = Array.isArray(data) ? data : [];
      // Instituição proprietária: mostra master e admin; demais: só admin
      setUsers(institution?.slug === 'master' ? all.filter(u => ['admin', 'master'].includes(u.role)) : all.filter(u => u.role === 'admin'));
    } catch (err) {
      setError(err.message || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }, [institution?.id]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formError) setFormError('');
  }, [formError]);

  const handleNewUser = useCallback(() => {
    setEditingUser(null);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setFormData({ name: '', email: '', password: '', confirmPassword: '' });
    setFormError('');
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((u) => {
    setEditingUser(u);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setFormData({ name: u.name, email: u.email, password: '', confirmPassword: '' });
    setFormError('');
    setShowModal(true);
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setFormError('');
    if (!formData.name?.trim()) {
      setFormError('Nome é obrigatório');
      return;
    }
    if (!formData.email?.trim()) {
      setFormError('Email é obrigatório');
      return;
    }
    if (!editingUser) {
      const pwdErr = validateNewPassword(formData.password);
      if (pwdErr) {
        setFormError(pwdErr);
        return;
      }
    }
    if (!editingUser && formData.password !== formData.confirmPassword) {
      setFormError('As senhas não coincidem');
      return;
    }
    if (editingUser && formData.password) {
      const pwdErr = validateNewPassword(formData.password);
      if (pwdErr) {
        setFormError(pwdErr);
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setFormError('As senhas não coincidem');
        return;
      }
    }
    try {
      setLoading(true);
      if (editingUser) {
        const payload = { name: formData.name.trim(), email: formData.email.trim(), role: editingUser.role || 'admin' };
        if (formData.password) payload.password = formData.password;
        await apiClient.updateMasterInstitutionUser(institution.id, editingUser.id, payload);
        addNotification('Sucesso', 'Administrador atualizado', 'success');
      } else {
        await apiClient.createMasterInstitutionUser(institution.id, {
          name: formData.name.trim(),
          email: formData.email.trim(),
          password: formData.password,
          role: 'admin',
        });
        addNotification('Sucesso', 'Administrador criado', 'success');
      }
      setShowModal(false);
      setEditingUser(null);
      loadUsers();
    } catch (err) {
      setFormError(err.message || 'Erro ao salvar');
    } finally {
      setLoading(false);
    }
  }, [formData, editingUser, institution?.id, loadUsers, addNotification]);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setEditingUser(null);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setFormError('');
  }, []);

  if (loading && users.length === 0) {
    return <LoadingSpinner text="Carregando usuários..." />;
  }

  return (
    <div style={styles.contentContainer}>
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={onBack}
              style={{ ...styles.button, ...styles.buttonSecondary, padding: '8px 12px' }}
              aria-label="Voltar"
            >
              ← Voltar
            </button>
            <h2 style={{ ...styles.cardTitle, margin: 0 }}>
            👥 {institution?.slug === 'master' ? 'Usuários (Proprietário e Admins)' : 'Administradores'} - {institution?.name || ''}
          </h2>
          </div>
          <button onClick={handleNewUser} style={{ ...styles.button, ...styles.buttonPrimary }}>
            ➕ Novo Administrador
          </button>
        </div>

        <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px' }}>
          {institution?.slug === 'master'
            ? 'Na instituição proprietária, o master pode alterar sua própria senha. Para outras instituições, cadastra apenas o administrador.'
            : 'O proprietário cadastra apenas o administrador da instituição. Analistas e demais usuários são criados pelo admin da instituição.'}
        </p>

        {error && <Alert message={error} type="error" onClose={() => setError('')} />}

        {users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
            <h3>Nenhum administrador cadastrado</h3>
            <p>Adicione o administrador para esta instituição acessar o sistema</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead style={styles.tableHeader}>
                <tr>
                  <th style={styles.tableHeaderCell}>Nome</th>
                  <th style={styles.tableHeaderCell}>Email</th>
                  <th style={styles.tableHeaderCell}>Status</th>
                  <th style={styles.tableHeaderCell}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} style={styles.tableRow}>
                    <td style={styles.tableCell}>
                      <div style={{ fontWeight: '600', color: '#1F2937' }}>{u.name}</div>
                    </td>
                    <td style={styles.tableCell}>{u.email}</td>
                    <td style={styles.tableCell}>
                      <span style={{
                        ...styles.statusBadge,
                        ...(u.role === 'master' ? { backgroundColor: '#EFF6FF', color: '#1D4ED8' } : styles.statusApproved)
                      }}>
                        {u.role === 'master' ? 'Proprietário' : 'Administrador'}
                      </span>
                    </td>
                    <td style={styles.tableCell}>
                      <button
                        onClick={() => handleEdit(u)}
                        style={{ ...styles.button, ...styles.buttonSecondary, ...styles.buttonSmall }}
                      >
                        ✏️ Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={editingUser ? 'Editar Administrador' : 'Novo Administrador'}
      >
        <form onSubmit={handleSubmit} style={styles.form}>
          {formError && (
            <div style={{ marginBottom: '16px' }}>
              <Alert message={formError} type="error" onClose={() => setFormError('')} />
            </div>
          )}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Nome Completo *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                style={styles.input}
                placeholder="Nome completo do administrador"
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email (login) *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                style={styles.input}
                placeholder="admin@instituicao.com"
                required
              />
            </div>
          </div>

          {!editingUser ? (
            <>
              <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 12px', lineHeight: 1.5 }}>{PASSWORD_POLICY_HINT}</p>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Senha *</label>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      style={{ ...styles.input, paddingRight: '40px', width: '100%' }}
                      placeholder="Mínimo 8 caracteres"
                      required
                    />
                    <EyeToggle visible={showPassword} onToggle={() => setShowPassword(v => !v)} />
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Confirmar Senha *</label>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      style={{ ...styles.input, paddingRight: '40px', width: '100%' }}
                      placeholder="Digite a senha novamente"
                      required
                    />
                    <EyeToggle visible={showConfirmPassword} onToggle={() => setShowConfirmPassword(v => !v)} />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 12px', lineHeight: 1.5 }}>{PASSWORD_POLICY_HINT}</p>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Nova senha (opcional)</label>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      style={{ ...styles.input, paddingRight: '40px', width: '100%' }}
                      placeholder="Deixe em branco para manter a atual"
                    />
                    <EyeToggle visible={showPassword} onToggle={() => setShowPassword(v => !v)} />
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Confirmar nova senha</label>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      style={{ ...styles.input, paddingRight: '40px', width: '100%' }}
                      placeholder="Repita a nova senha"
                    />
                    <EyeToggle visible={showConfirmPassword} onToggle={() => setShowConfirmPassword(v => !v)} />
                  </div>
                </div>
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
            <button type="button" onClick={closeModal} style={{ ...styles.button, ...styles.buttonSecondary }}>
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{ ...styles.button, ...styles.buttonPrimary, opacity: loading ? 0.7 : 1 }}
            >
              {loading ? 'Salvando...' : (editingUser ? 'Atualizar' : 'Criar')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
});
